import { z } from 'zod';
import { currencySchema, i18nTextSchema, moneySchema } from './common.js';

/**
 * Contracts for the v2 growth modules: promotions, affiliates and broadcasts.
 */

/* ------------------------------------------------------------------ */
/* Promotion                                                           */
/* ------------------------------------------------------------------ */

export const promotionScopeSchema = z.enum(['order', 'product', 'category', 'shipping']);
export const customerSegmentSchema = z.enum(['all', 'new', 'returning']);
export const effectKindSchema = z.enum(['percent_off', 'fixed_off', 'free_shipping']);

export const promotionConditionSchema = z.object({
  scope: promotionScopeSchema.default('order'),
  productIds: z.array(z.string().uuid()).max(200).default([]),
  categoryIds: z.array(z.string().uuid()).max(50).default([]),
  minSubtotal: z.number().int().min(0).nullable().default(null),
  startsAt: z.string().datetime().nullable().default(null),
  endsAt: z.string().datetime().nullable().default(null),
  customerSegment: customerSegmentSchema.default('all'),
  usageLimitTotal: z.number().int().min(1).nullable().default(null),
  usageLimitPerCustomer: z.number().int().min(1).nullable().default(null),
});

export const promotionEffectSchema = z
  .object({
    kind: effectKindSchema,
    value: z.number().int().min(0),
    maxDiscount: z.number().int().min(0).nullable().default(null),
  })
  .refine((effect) => effect.kind !== 'percent_off' || effect.value <= 100, {
    message: 'A percentage discount cannot exceed 100',
  });

export const upsertPromotionSchema = z.object({
  name: i18nTextSchema,
  /**
   * Stored uppercased and matched case-insensitively; null makes the
   * promotion automatic. Restricted to deep-link-safe characters because
   * `?start=promo_X` carries it (§3).
   */
  code: z
    .string()
    .regex(/^[A-Za-z0-9_-]{3,32}$/, 'Codes use letters, numbers, dash and underscore')
    .nullable()
    .default(null),
  condition: promotionConditionSchema,
  effect: promotionEffectSchema,
  isActive: z.boolean().default(true),
  priority: z.number().int().min(0).max(100).default(0),
});
export type UpsertPromotionInput = z.infer<typeof upsertPromotionSchema>;

export const promotionSchema = z.object({
  id: z.string().uuid(),
  name: i18nTextSchema,
  code: z.string().nullable(),
  condition: promotionConditionSchema,
  effect: promotionEffectSchema,
  isActive: z.boolean(),
  priority: z.number().int(),
  /** Redemption count and total discount granted, for the campaign view. */
  redemptionCount: z.number().int(),
  discountGranted: moneySchema.nullable(),
  createdAt: z.string().datetime(),
});
export type PromotionDto = z.infer<typeof promotionSchema>;

/** What the storefront shows about a code the customer typed. */
export const appliedPromotionSchema = z.object({
  id: z.string().uuid(),
  name: i18nTextSchema,
  code: z.string().nullable(),
  amount: z.number().int(),
  freeShipping: z.boolean(),
});

/* ------------------------------------------------------------------ */
/* Affiliate                                                           */
/* ------------------------------------------------------------------ */

export const affiliateStatusSchema = z.enum(['active', 'paused', 'removed']);
export const commissionStatusSchema = z.enum(['pending', 'approved', 'paid', 'void']);

export const upsertAffiliateSchema = z.object({
  name: z.string().min(2).max(80),
  code: z
    .string()
    .regex(/^[A-Za-z0-9_-]{3,32}$/, 'Codes use letters, numbers, dash and underscore'),
  telegramUsername: z.string().max(40).nullable().optional(),
  phone: z.string().max(24).nullable().optional(),
  /** Basis points: 500 = 5%. Integers only — no floats in money maths. */
  commissionBps: z.number().int().min(0).max(5000).default(500),
  minPayoutAmount: z.number().int().min(0).default(0),
  status: affiliateStatusSchema.default('active'),
  note: z.string().max(500).nullable().optional(),
});
export type UpsertAffiliateInput = z.infer<typeof upsertAffiliateSchema>;

export const affiliateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  telegramUsername: z.string().nullable(),
  phone: z.string().nullable(),
  commissionBps: z.number().int(),
  minPayoutAmount: z.number().int(),
  status: affiliateStatusSchema,
  /** Referral link the affiliate shares. */
  shareUrl: z.string().url(),
  botLink: z.string().nullable(),
  stats: z.object({
    orders: z.number().int(),
    pending: moneySchema,
    approved: moneySchema,
    paid: moneySchema,
  }),
  createdAt: z.string().datetime(),
});
export type AffiliateDto = z.infer<typeof affiliateSchema>;

export const commissionSchema = z.object({
  id: z.string().uuid(),
  affiliateId: z.string().uuid(),
  affiliateName: z.string(),
  orderId: z.string().uuid(),
  orderCode: z.string(),
  baseAmount: moneySchema,
  commissionBps: z.number().int(),
  amount: moneySchema,
  status: commissionStatusSchema,
  approvedAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  payoutReference: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type CommissionDto = z.infer<typeof commissionSchema>;

export const payCommissionsSchema = z.object({
  affiliateId: z.string().uuid(),
  commissionIds: z.array(z.string().uuid()).min(1).max(500),
  reference: z.string().max(80),
});

/**
 * §3 keys commission approval to the return window: a commission becomes
 * approved once the customer can no longer send the goods back.
 */
export const COMMISSION_RETURN_WINDOW_DAYS = 7;

/** §3: last-click attribution with a 7-day window. */
export const AFFILIATE_ATTRIBUTION_WINDOW_DAYS = 7;

/* ------------------------------------------------------------------ */
/* Broadcast + automation                                              */
/* ------------------------------------------------------------------ */

export const broadcastSegmentSchema = z.enum([
  'all',
  'with_orders',
  'no_orders',
  'recent_60d',
  'dormant_60d',
]);
export type BroadcastSegment = z.infer<typeof broadcastSegmentSchema>;

export const upsertBroadcastSchema = z.object({
  name: z.string().min(2).max(80),
  body: i18nTextSchema,
  buttonLabel: i18nTextSchema.nullable().optional(),
  buttonUrl: z.string().url().nullable().optional(),
  segment: broadcastSegmentSchema.default('all'),
  scheduledFor: z.string().datetime().nullable().optional(),
});
export type UpsertBroadcastInput = z.infer<typeof upsertBroadcastSchema>;

export const broadcastSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  body: i18nTextSchema,
  segment: broadcastSegmentSchema,
  status: z.enum(['draft', 'queued', 'sending', 'sent', 'failed', 'blocked', 'cancelled']),
  recipientCount: z.number().int(),
  sentCount: z.number().int(),
  failedCount: z.number().int(),
  blockedCount: z.number().int(),
  scheduledFor: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type BroadcastDto = z.infer<typeof broadcastSchema>;

export const automationSettingsSchema = z.object({
  paymentReminderEnabled: z.boolean().default(true),
  paymentReminderMinutesBefore: z.number().int().min(5).max(240).default(20),
  winBackEnabled: z.boolean().default(false),
  winBackAfterDays: z.number().int().min(7).max(365).default(45),
  winBackBody: i18nTextSchema.nullable().default(null),
  /** No merchant wants their customers messaged at 2am. */
  quietHoursStart: z.string().regex(/^\d{2}:\d{2}$/).default('21:00'),
  quietHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).default('08:00'),
});
export type AutomationSettingsInput = z.infer<typeof automationSettingsSchema>;

/* ------------------------------------------------------------------ */
/* Analytics                                                           */
/* ------------------------------------------------------------------ */

export const analyticsRangeSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  /** Bucket size for the revenue series. */
  granularity: z.enum(['day', 'week']).default('day'),
});

export const analyticsSchema = z.object({
  currency: currencySchema,
  range: z.object({ from: z.string().datetime(), to: z.string().datetime() }),
  revenue: z.object({
    gross: moneySchema,
    orders: z.number().int(),
    averageOrder: moneySchema,
    /** Discounts granted in the period — what promotions actually cost. */
    discounts: moneySchema,
  }),
  series: z.array(
    z.object({ bucket: z.string(), revenue: z.number().int(), orders: z.number().int() }),
  ),
  paymentSplit: z.array(
    z.object({ paymentType: z.enum(['prepaid', 'cod']), orders: z.number().int(), revenue: z.number().int() }),
  ),
  /** §3 `analytics`: COD reporting is a first-class report, not a filter. */
  cod: z.object({
    shipped: moneySchema,
    collected: moneySchema,
    remitted: moneySchema,
    outstanding: moneySchema,
  }),
  topProducts: z.array(
    z.object({
      productId: z.string().uuid().nullable(),
      name: i18nTextSchema,
      units: z.number().int(),
      revenue: z.number().int(),
    }),
  ),
  topVariants: z.array(
    z.object({
      label: z.string(),
      units: z.number().int(),
    }),
  ),
  acquisition: z.array(
    z.object({ source: z.string(), orders: z.number().int(), revenue: z.number().int() }),
  ),
});
export type AnalyticsDto = z.infer<typeof analyticsSchema>;
